import { memo, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { setMember } from '../../store/member/MemberActions'
import { Avatar, Badge, CalendarIcon, ChevronRightIcon, cn } from '@quickmd-llc/design-system'
import { MANAGEMENT_LABEL, MANAGEMENT_PAGE_ROUTE } from '../../utils/constants'
import { setCalendarOffsetBeforeNewVisit, setCalendarRangeBeforeNewVisit } from '../../store/calendar/CalendarActions'
import { ExpandableList } from './components/expandable-list/ExpandableList'
import { useServiceTypes } from '../../hooks/useServiceTypes'
import type {
  UserVisitTypeListItemAdminApiOutContract,
  ServiceTypeListItemAdminApiOutContract,
} from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterOptions = { visitTypes: string; states: string }

type Props = {
  firstName: string | null | undefined
  id: string | null | undefined
  lastName: string | null | undefined
  prefix: string | null | undefined
  suffix: string | null | undefined
  email: string | null | undefined
  role: string | null | undefined
  photo: string | null | undefined
  visitTypes: UserVisitTypeListItemAdminApiOutContract[] | null | undefined
  pending: boolean | null | undefined
  isAccountActive: boolean | null | undefined
  defaultTimeZone: string | null
  filterOptions: FilterOptions
  visitTypeVisibilityOverrideStatus: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

type NameParts = {
  firstName: string | null | undefined
  lastName: string | null | undefined
  prefix: string | null | undefined
  suffix: string | null | undefined
}

const getFullName = ({ firstName, lastName, prefix, suffix }: NameParts): string => {
  let author = prefix ? `${prefix} ` : ''
  author += firstName ? `${firstName} ` : ''
  author += lastName ? `${lastName} ` : ''
  author += suffix ? `${suffix} ` : ''
  return author
}

type FormattedVisitTypes = { visitTypeNames: string[]; states: string[] }

const formatVisitTypes = (
  visitTypes: UserVisitTypeListItemAdminApiOutContract[],
  serviceTypes: ServiceTypeListItemAdminApiOutContract[],
): FormattedVisitTypes => {
  let states: string[] = []
  let visitTypeNames: string[] = visitTypes.map(({ VisitTypeName, StateCodes }) => {
    states = states.concat(StateCodes ?? [])
    return VisitTypeName ?? ''
  })
  states = [...new Set(states)]
  visitTypeNames = [...new Set(visitTypeNames)]
  visitTypeNames = visitTypeNames
    .map((name) => {
      for (const serviceType of serviceTypes) {
        for (const vt of serviceType.VisitTypes ?? []) {
          if (vt.Name === name && vt.VisibilityType === 'all') return name
        }
      }
      return undefined
    })
    .filter((item): item is string => item !== undefined)
  return { visitTypeNames, states }
}

// ExpandableList expects non-nullable VisitTypeName and StateCodes — filter them here.
type ExpandableVisitType = { VisitTypeName: string; StateCodes: string[] }

const capitalizeRole = (role: string | null | undefined): string => {
  if (!role) return ''
  return role.charAt(0).toUpperCase() + role.slice(1)
}

const toExpandableVisitTypes = (
  visitTypes: UserVisitTypeListItemAdminApiOutContract[] | null | undefined,
): ExpandableVisitType[] =>
  (visitTypes ?? []).flatMap((vt) => {
    if (vt.VisitTypeName == null) return []
    return [{ VisitTypeName: vt.VisitTypeName, StateCodes: vt.StateCodes ?? [] }]
  })

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/*
 * SCSS → Tailwind mapping (ClinicMember.scss deleted):
 *
 *  .clinic-member-row
 *    display:flex / align-items:center / gap:24px / padding:16px 24px
 *    background:#ffffff / cursor:pointer
 *    border-bottom:1px solid var(--border-card)        → border-b border-shade-10
 *    :last-child border-bottom:none / rounded 0 0 8px 8px
 *                                                      → last:border-b-0 last:rounded-b-large
 *    :hover  linear-gradient(rgba(0,162,178,0.05))     → hover:bg-[#f2fafb]
 *       (#f2fafb = rgba(0,162,178,0.05) over #fff — no DS token; same value used in ServiceTypeRow)
 *    NOTE: "clinic-member-row" kept as a plain selector anchor for E2E Playwright tests
 *
 *  .cm-col
 *    display:flex / align-items:center / overflow:hidden / flex-shrink:0 → flex items-center overflow-hidden shrink-0
 *
 *  .cm-col-name  flex:1 1 0 / min-width:0 / gap:12px   → flex-1 min-w-0 gap-sp-1.5
 *  (Utilization column removed — no utilization data yet; header drops it in lockstep.)
 *  .cm-col-email  flex:1 1 0 / min-width:0              → flex-1 min-w-0
 *  .cm-col-licenses  flex:0 0 176px                     → w-[176px] (no DS token for 176px)
 *  .cm-col-services  flex:0 0 300px                     → w-[300px] (no DS token for 300px)
 *  .cm-col-status   flex:0 0 96px                       → w-[96px]  (no DS token for 96px)
 *  .cm-col-arrow    flex:0 0 24px / justify-content:flex-end → w-sp-3 justify-end
 *
 *  .cm-avatar-wrapper
 *    position:relative / flex-shrink:0 / width/height:40px → relative shrink-0 w-sp-5 h-sp-5
 *
 *  .cm-online-dot
 *    position:absolute / bottom:1px / right:1px / width/height:10px
 *    border-radius:50% / background:#00c48c / border:2px solid #fff
 *    → absolute bottom-[1px] right-[1px] w-[10px] h-[10px] rounded-full bg-[#00c48c] border-2 border-white
 *    (#00c48c = success green — no DS color token; arbitrary value retained with comment)
 *
 *  .cm-name-block
 *    display:flex / flex-direction:column / gap:2px / min-width:0 → flex flex-col gap-sp-0.25 min-w-0
 *
 *  .cm-name-text
 *    font:14px/20px 'Outfit' 600 / color:var(--text-dark) → text-size-3 font-semibold leading-5 text-text-dark
 *    white-space:nowrap / overflow:hidden / text-overflow:ellipsis → truncate whitespace-nowrap
 *    display:flex / align-items:center / gap:6px        → flex items-center gap-sp-0.75
 *
 *  .cm-calendar-link
 *    display:inline-flex / align-items:center / gap:4px → inline-flex items-center gap-sp-0.5
 *    background:none / border:none / padding:0 / cursor:pointer → bg-transparent border-none p-0 cursor-pointer
 *    font:12px/16px 'Outfit' 400 / color:var(--highlight) → text-size-2 font-normal leading-4 text-text-highlight
 *    :hover text-decoration:underline → hover:underline
 *
 *  .cm-email-text / .cm-util-value / .cm-chevron
 *    color:var(--text-medium) → text-text-light  (DS token for rgba(37,56,69,0.7))
 *    email: font:14px/? 400 → text-size-3 font-normal
 *    util: font:14px 400 → text-size-3 font-normal
 *    both: white-space:nowrap overflow:hidden text-overflow:ellipsis → truncate whitespace-nowrap (email only)
 *
 *  .clinic-member-visibility-tag
 *    color:var(--text-inverse)=#f7f8fc → text-text-inverse
 *    font:11px/16px 'Outfit' 600 / inline-flex / padding:2px 8px / border-radius:100px
 *    background:var(--text-disabled)=rgba(37,56,69,0.40) → bg-text-disabled
 *    white-space:nowrap
 *    → inline-flex items-center justify-center rounded-full py-sp-0.25 px-sp-1
 *      text-[11px] font-semibold leading-4 text-text-inverse bg-text-disabled whitespace-nowrap
 *    (11px — no DS size token between size-1=10px and size-2=12px; arbitrary value retained)
 */

export const ClinicMember = memo(
  ({
    photo,
    firstName,
    lastName,
    prefix,
    suffix,
    id,
    email,
    role,
    visitTypes,
    pending,
    isAccountActive,
    defaultTimeZone,
    filterOptions,
    visitTypeVisibilityOverrideStatus,
  }: Props) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { serviceTypes } = useServiceTypes()

    const expandableVisitTypes = useMemo(() => toExpandableVisitTypes(visitTypes), [visitTypes])

    const formattedVisitTypes = useMemo(
      () => formatVisitTypes(expandableVisitTypes, serviceTypes),
      [expandableVisitTypes, serviceTypes],
    )

    const providerName = getFullName({ firstName, lastName, prefix, suffix })

    const handleOpenMember = (isCalendar?: boolean) => {
      dispatch(
        setMember({
          FirstName: firstName,
          LastName: lastName,
          Prefix: prefix,
          Suffix: suffix,
          UserId: id,
          Email: email,
          UserType: capitalizeRole(role),
          isVerified: pending,
          isAccountActive,
        }),
      )

      const searchParams = new URLSearchParams({
        id: id ?? '',
        type: capitalizeRole(role),
        tab: isCalendar ? 'Calendar' : 'General',
      })

      void navigate(`/management/member?${searchParams.toString()}`, {
        state: {
          search: location.search,
          providerId: role === 'provider' ? id : undefined,
          providerName,
          ImageUrl: photo,
          timezone: defaultTimeZone ?? currentTimeZone,
          label: MANAGEMENT_LABEL,
          calendarRoute: '/management/member',
          route: MANAGEMENT_PAGE_ROUTE,
        },
      })
    }

    const handleClickShowCalendar = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      dispatch(setCalendarRangeBeforeNewVisit())
      dispatch(setCalendarOffsetBeforeNewVisit(null))
      handleOpenMember(true)
    }

    const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleOpenMember()
      }
    }

    const showCalendarLink = !pending && role === 'provider'
    const isAdmin = role === 'admin'

    const getStatus = (): 'Active' | 'Unverified' | 'Inactive' => {
      if (pending) return 'Unverified'
      if (!isAccountActive) return 'Inactive'
      return 'Active'
    }

    const status = getStatus()
    const hasVisitTypes = expandableVisitTypes.length > 0

    return (
      <div
        id={id ?? undefined}
        role='button'
        tabIndex={0}
        className={cn(
          'clinic-member-row',
          'flex cursor-pointer items-center gap-sp-3 bg-white px-sp-3 py-sp-2',
          'border-b border-shade-10 last:rounded-b-large last:border-b-0',
          'hover:bg-[#f2fafb]',
        )}
        onClick={() => handleOpenMember()}
        onKeyDown={handleRowKeyDown}>
        {/* Name column */}
        <div className='flex min-w-0 flex-1 shrink-0 items-center gap-sp-1.5 overflow-hidden'>
          <div className='relative h-sp-5 w-sp-5 shrink-0'>
            <Avatar size='s' name={providerName} {...(photo ? { src: photo } : {})} />
            {isAccountActive && !pending ?
              <span className='absolute bottom-[1px] right-[1px] h-[10px] w-[10px] rounded-full border-2 border-white bg-[#00c48c]' />
            : null}
          </div>
          <div className='flex min-w-0 flex-col gap-sp-0.25'>
            <div className='flex items-center gap-sp-0.75 overflow-hidden text-ellipsis whitespace-nowrap text-size-3 font-semibold leading-5 text-text-dark'>
              {prefix} {firstName} {lastName} {suffix}
              {visitTypeVisibilityOverrideStatus ?
                <span className='inline-flex items-center justify-center whitespace-nowrap rounded-full bg-text-disabled px-sp-1 py-sp-0.25 text-[11px] font-semibold leading-4 text-text-inverse'>
                  {visitTypeVisibilityOverrideStatus}
                </span>
              : null}
            </div>
            {showCalendarLink ?
              <button
                type='button'
                className='inline-flex cursor-pointer items-center gap-sp-0.5 border-none bg-transparent p-0 text-size-2 font-normal leading-4 text-text-highlight hover:underline'
                onClick={handleClickShowCalendar}>
                <CalendarIcon iconSize='s' className='shrink-0 text-text-highlight' />
                <span>View calendar</span>
              </button>
            : null}
          </div>
        </div>

        {
          isAdmin ?
            /* Email column (admin view) */
            <div className='flex min-w-0 flex-1 shrink-0 items-center overflow-hidden'>
              <span className='overflow-hidden text-ellipsis whitespace-nowrap text-size-3 font-normal text-text-light'>
                {email}
              </span>
            </div>
            /* Provider-only columns. Utilization column hidden until real utilization data exists
             (was a `--` placeholder); header (ClinicMembers) drops it in lockstep. */
          : <>
              <div className='flex w-[96px] shrink-0 items-center overflow-hidden xl:w-[176px] xl:min-w-[100px] xl:shrink'>
                <ExpandableList
                  items={hasVisitTypes ? formattedVisitTypes.states : []}
                  filterOptions={filterOptions}
                  visitTypes={expandableVisitTypes}
                  type='states'
                />
              </div>
              <div className='flex w-[96px] shrink-0 items-center overflow-hidden xl:w-[300px] xl:min-w-[160px] xl:shrink'>
                <ExpandableList
                  items={hasVisitTypes ? formattedVisitTypes.visitTypeNames : []}
                  filterOptions={filterOptions}
                  visitTypes={expandableVisitTypes}
                  type='visits'
                />
              </div>
            </>

        }

        {/* Status column */}
        <div className='flex w-[96px] shrink-0 items-center overflow-hidden'>
          <Badge
            variant={
              status === 'Active' ? 'active'
              : status === 'Unverified' ?
                'warning'
              : 'idle'
            }
            size='s'>
            {status}
          </Badge>
        </div>

        {/* Chevron */}
        <div className='flex w-sp-3 shrink-0 items-center justify-end overflow-hidden'>
          <ChevronRightIcon iconSize='s' className='text-text-light' />
        </div>
      </div>
    )
  },
)
